import { type RealtimeEvent } from '@shared/ui/realtime/realtime-event';
import {
	isStaleVersion,
	realtimeHandlers,
	useRealtimeEvents,
} from '@shared/ui/realtime/use-realtime-events';
import { toast } from '@shared/ui/toast/toast-store';
import { useQueryClient } from '@tanstack/react-query';

import { apiOrigin } from '../core/api';
import { useMe } from '../core/session.hooks';
import { scopeKey } from './intake.hooks';
import { appendScopeProgress, type ScopeProgress } from './progress.hooks';
import { type Scope, scopesKey } from './scopes.hooks';

/**
 * Reconcile scope lifecycle broadcasts into the react-query cache, so a change made
 * anywhere in the workspace — a teammate's edit, or an agent's write over MCP —
 * appears live, not just on the tab that made it (PLAN-46). Mounted once at the app
 * root so a single socket serves the list, the detail, and every stage; the shared
 * hook drops our own echoes and refetches on reconnect.
 *
 * This is what lets the BYOA hand-off stop polling: when the user's agent writes the
 * questions over MCP, the api broadcasts `scope.updated` with the full scope, we
 * merge it into `scopeKey`, and the open Describe page navigates on to Questions.
 */
// The scope channel's event → payload contract (TAS-150): create/update carry the
// (possibly summary-only) scope, progress carries a narration entry, delete the id.
interface ScopeRealtimeEvents {
	'scope.created': Scope;
	'scope.updated': Scope;
	'scope.deleted': { id: string };
	'scope.progress': ScopeProgress;
}

export function useScopeRealtime(): void {
	const queryClient = useQueryClient();
	const { data: me } = useMe();

	// Merge an updated/created scope into the caches. Some `scope.updated` payloads
	// carry the full scope (sections and all), others only the summary the list shows;
	// a shallow merge keeps fields the payload omits (undefined keys drop out over the
	// wire), and the version guard ignores stale, out-of-order events.
	function upsert(event: RealtimeEvent<Scope>): void {
		const scope = event.data;
		if (!scope?.id) {
			return;
		}
		// Detail cache: only touch an entry that already exists — don't seed a partial
		// scope from a list-level broadcast (the detail page fetches its own full read).
		queryClient.setQueryData<Scope>(scopeKey(scope.id), (prev) =>
			prev === undefined || isStaleVersion(prev, scope) ? prev : { ...prev, ...scope },
		);
		// List cache: replace the row in place, or prepend a newly-created one.
		queryClient.setQueryData<Scope[]>(scopesKey, (prev) => {
			if (!prev) {
				return prev;
			}
			const index = prev.findIndex((row) => row.id === scope.id);
			if (index === -1) {
				return [scope, ...prev];
			}
			if (isStaleVersion(prev[index], scope)) {
				return prev;
			}
			const next = [...prev];
			next[index] = { ...next[index], ...scope };
			return next;
		});
	}

	// Append an agent's progress narration (a `scope.progress` broadcast) to the scope's
	// live log, so the open hand-off modal shows the agent working. Not versioned —
	// progress is append-only; the agent's event carries no mutationId to echo-drop.
	function progress(event: RealtimeEvent<ScopeProgress>): void {
		const entry = event.data;
		if (!entry?.scopeId) {
			return;
		}
		appendScopeProgress(queryClient, entry);
		// Toast when an agent's WRITE lands (PLAN-99), so the user sees it even away from the
		// hand-off modal — "<Agent> answered 2 questions". Reads ("Read the …") are just
		// timeline narration and don't toast; a person acting directly carries no agent.
		if (entry.actor?.agent && !entry.label.startsWith('Read')) {
			toast({ variant: 'info', title: entry.actor.agent, description: entry.label });
		}
	}

	// Drop a deleted scope from both caches (the payload is just `{ id }`).
	function remove(event: RealtimeEvent<{ id: string }>): void {
		const data = event.data;
		if (!data?.id) {
			return;
		}
		queryClient.removeQueries({ queryKey: scopeKey(data.id) });
		queryClient.setQueryData<Scope[]>(scopesKey, (prev) =>
			prev ? prev.filter((row) => row.id !== data.id) : prev,
		);
	}

	useRealtimeEvents({
		url: apiOrigin,
		enabled: Boolean(me),
		handlers: realtimeHandlers<ScopeRealtimeEvents>({
			'scope.created': upsert,
			'scope.updated': upsert,
			'scope.deleted': remove,
			'scope.progress': progress,
		}),
		// Revalidate the list and any open scope on EVERY connect — the first one
		// included, not just reconnects. Scope paints from a persisted cache, so the
		// tab often opens showing a stale copy (an agent may have written the questions
		// or docs while it was closed); refetching the moment the socket is live catches
		// that up before the page routes off the stale data (PLAN-88). `connect` fires on
		// reconnection too, so this also covers what the old `onReconnect` did.
		onConnect: () => {
			void queryClient.invalidateQueries({ queryKey: scopesKey });
			void queryClient.invalidateQueries({ queryKey: ['scope'] });
		},
	});
}
